import { Link } from '@tanstack/react-router';
import { Fuel, Truck } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { totalSobreTudo } from '@/componentes/totais';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import {
  formatarData,
  formatarDataHora,
  formatarDecimal,
  formatarInteiro,
  formatarReais,
  interpretarDataHora,
  interpretarNumero,
  paraCampoDataHora,
} from '@/lib/formato';

import { useApoioDoEstoque } from '@/modulos/estoque/consultas';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';
import {
  explicarFalha,
  useAbastecimentos,
  useCadastros,
  useLubrificantes,
  useRegistrarLubrificante,
} from './consultas';
import { comErroDe, conferirTodos, type ErrosDeCampo, marcaDeErro, NENHUM_ERRO, temErro } from './erros-de-campo';
import {
  agregarPorAtivo,
  calcularLancamentos,
  estaForaDaFaixa,
  ladoDoDesvio,
  textoDaFaixa,
  type ConsumoPorAtivo,
} from './consumo';
import type { AplicacaoDeLubrificante, EquipamentoDoModulo } from './tipos';
import { HubDoCombustivel } from './HubDoCombustivel';
import { AcoesNoRodape, JanelaDeAcaoComRodape } from './RodapeDaJanela';

/**
 * Consumo e lubrificantes — a ferramenta 4 do módulo 08.
 *
 * ## O aviso do documento virou coluna
 *
 * O v6 é explícito: "consumo exige períodos equivalentes; sem balanço
 * confiável, mostrar como estimativa" e "sinaliza limitações quando faltam
 * leituras". A view `comb.consumo_abastecimento` responde isso por lançamento,
 * e esta tela agrega por MÁQUINA — dizendo, em cada linha, quantos lançamentos
 * entraram na conta e quantos ficaram de fora.
 *
 * Linha com pouca base NÃO é escondida. Esconder faria a média da frota parecer
 * melhor do que é; mostrá-la com a base ao lado deixa quem lê decidir se
 * confia.
 *
 * ## Lubrificante é fato, não baixa de estoque
 *
 * Registrar a aplicação aqui não movimenta almoxarifado nenhum: o saldo por
 * depósito é do módulo 07, que não existe. A tela diz isso onde a pessoa vai
 * registrar, e não numa nota de rodapé — esconder produziria um saldo que
 * ninguém mantém.
 */

const COLUNAS: readonly Coluna<ConsumoPorAtivo>[] = [
  {
    chave: 'equipamento',
    rotulo: 'Equipamento',
    celula: (c) => (
      <>
        <span className="codigo block text-tinta">{c.codigo}</span>
        <TextoDaCelula texto={c.tipo ?? 'Fora da frota'} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </>
    ),
  },
  {
    chave: 'litros',
    rotulo: 'Litros',
    alinhamento: 'numero',
    celula: (c) => <Medida valor={formatarInteiro(c.litros)} unidade="L" />,
    total: (linhas) => <Medida valor={formatarInteiro(linhas.reduce((s, c) => s + c.litros, 0))} unidade="L" />,
  },
  {
    chave: 'consumo',
    rotulo: 'Consumo médio',
    historia: 'NUM-CMB-08',
    alinhamento: 'numero',
    /* Fora da faixa, o consumo médio é o próprio alerta: vermelho (R02, p.31;
       regra 14). A coluna ao lado diz de que lado ele saiu — cor nunca sozinha. */
    celula: (c) =>
      c.consumoMedio === null ? (
        <span className="numero text-tinta-secundaria">Sem base de comparação</span>
      ) : (
        <>
          {estaForaDaFaixa(c.consumoMedio, { minimo: c.faixaMinimo, maximo: c.faixaMaximo }) ? (
            <span className="numero text-parada">
              {formatarDecimal(c.consumoMedio, 1)}
              <span className="text-tinta-secundaria"> {c.unidade}</span>
            </span>
          ) : (
            <Medida valor={formatarDecimal(c.consumoMedio, 1)} unidade={c.unidade} />
          )}
          <span className="txt-linha2 block text-tinta-secundaria">
            {formatarInteiro(c.comBase)} de {formatarInteiro(c.lancamentos)} lançamentos na conta
          </span>
        </>
      ),
  },
  {
    chave: 'faixa',
    /* "Faixa esperada", e não "do modelo": desde a decisão 8 do dono a faixa é
       da UNIDADE, e a da configuração vale como herança. O rótulo antigo
       mentia justamente na máquina que tem faixa própria. */
    rotulo: 'Faixa esperada',
    alinhamento: 'numero',
    /* A avaliação vai ESCRITA na segunda linha; a cor de alerta fica no
       número que saiu da faixa, o consumo médio da coluna ao lado. */
    celula: (c) => {
      /* UMA PONTA SÓ JÁ É FAIXA: o banco julga o desvio com teto sozinho ou
         piso sozinho, e a coluna escreve o que ele julga. Quem escreve é
         `textoDaFaixa`, em `consumo.ts`, porque três telas dizem a mesma
         frase.
         COM A UNIDADE, em cinza (p.12): o cabeçalho não a declara, e a coluna
         mistura L/h de escavadeira com L/km de caminhão. */
      const escrita = textoDaFaixa(c.faixaMinimo, c.faixaMaximo);
      return escrita === null ? (
        <span className="numero text-tinta-secundaria">Sem faixa cadastrada</span>
      ) : (
        <>
          <span className="numero block text-tinta">
            {escrita}
            <span className="text-tinta-secundaria"> {c.unidade}</span>
          </span>
          <span className="txt-linha2 block text-tinta-secundaria">{avaliacaoDaFaixa(c)}</span>
        </>
      );
    },
  },
  {
    chave: 'ultimo',
    rotulo: 'Último abastecimento',
    /* Data não quebra (p.18). */
    quebra: 'nunca',
    celula: (c) => (c.ultimoEm === null ? '—' : formatarData(c.ultimoEm)),
  },
];

function avaliacaoDaFaixa(c: ConsumoPorAtivo): string {
  if (c.consumoMedio === null) return 'sem consumo para comparar';
  if (c.faixaMinimo === null && c.faixaMaximo === null) return 'sem faixa para comparar';
  /* A MESMA REGRA DO SERVIDOR, e por isso ela vem de `consumo.ts`: escrever
     `> maximo || < minimo` aqui daria um terceiro lugar decidindo desvio, e o
     terceiro lugar é o que nunca recebe a correção. */
  const faixa = { minimo: c.faixaMinimo, maximo: c.faixaMaximo };
  if (!estaForaDaFaixa(c.consumoMedio, faixa)) return 'dentro da faixa';
  return `${ladoDoDesvio(c.consumoMedio, faixa)} da faixa`;
}

const COLUNAS_LUBRIFICANTE: readonly Coluna<AplicacaoDeLubrificante>[] = [
  {
    chave: 'equipamento',
    rotulo: 'Equipamento',
    celula: (a) => <span className="codigo text-tinta">{a.equipamentoCodigo}</span>,
  },
  { chave: 'quando', rotulo: 'Quando', quebra: 'nunca', celula: (a) => formatarDataHora(a.quando) },
  {
    chave: 'produto',
    rotulo: 'Produto',
    celula: (a) => (
      <>
        <TextoDaCelula texto={a.produto} classe="txt-corpo text-tinta" linhas={1} />
        <TextoDaCelula texto={a.ponto ?? 'Sem ponto informado'} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </>
    ),
  },
  {
    chave: 'quantidade',
    rotulo: 'Quantidade',
    alinhamento: 'numero',
    celula: (a) => <Medida valor={formatarDecimal(a.quantidade, 1)} unidade={a.unidade} />,
  },
  {
    /* O VALOR DA APLICAÇÃO, que até a 0011 não existia em lugar nenhum: o
       custo de lubrificante era zero no sistema, e quem lia um custo por hora
       lia um número menor que o real sem ter como saber. "Sem preço" é o que
       a linha antiga diz — e dizer isso é mais honesto do que mostrar R$ 0,00. */
    chave: 'valor',
    rotulo: 'Valor',
    alinhamento: 'numero',
    celula: (a) =>
      a.valorTotal === null || a.valorTotal === undefined ? (
        <span className="numero text-tinta-secundaria">Sem preço</span>
      ) : (
        <span className="text-tinta">{formatarReais(a.valorTotal)}</span>
      ),
  },
  {
    chave: 'responsavel',
    rotulo: 'Responsável',
    celula: (a) => a.responsavel ?? 'Não informado',
  },
];

export function PaginaConsumo() {
  const [registrando, setRegistrando] = useState(false);
  /* Registrar lubrificante e lançar abastecimento: só para quem tem a licença
     de cada um (prova do marco, 07/10/2026). */
  const permissoesDaSessao = useSessaoEmMemoria()?.permissoes ?? [];
  const podeRegistrarLubrificante = permissoesDaSessao.includes('lubrificante.registrar');
  const podeLancar = permissoesDaSessao.includes('abastecimento.registrar');
  const consultaAbastecimentos = useAbastecimentos();
  const consultaCadastros = useCadastros();
  const consultaLubrificantes = useLubrificantes();

  /* `?? []` cria um array novo a cada render, e um array novo entra como
     dependencia diferente no `useMemo` de baixo — a agregacao inteira
     recalcularia a cada tecla digitada no formulario da janela. */
  const abastecimentos = useMemo(
    () => consultaAbastecimentos.data?.linhas ?? [],
    [consultaAbastecimentos.data],
  );
  const equipamentos = useMemo(
    () => consultaCadastros.data?.equipamentos ?? [],
    [consultaCadastros.data],
  );
  const lubrificantes = consultaLubrificantes.data?.linhas ?? [];
  const paginaDosLubrificantes = usePaginacao(lubrificantes);
  /* Os dois cards e a linha do recorte leem a lista JÁ cruzada com os
     equipamentos: sem as duas consultas, os números deles não existem. */
  const carregandoConsumo = consultaAbastecimentos.isPending || consultaCadastros.isPending;

  const porAtivo = useMemo(
    () => agregarPorAtivo(calcularLancamentos(abastecimentos, equipamentos), equipamentos),
    [abastecimentos, equipamentos],
  );

  const paginaDoConsumo = usePaginacao(porAtivo);

  const comBase = porAtivo.filter((c) => c.consumoMedio !== null).length;
  const foraDaFaixa = porAtivo.filter(
    (c) => c.consumoMedio !== null && avaliacaoDaFaixa(c).startsWith('a'),
  ).length;

  /* O RECORTE da conta. A lista vem cortada pelo servidor, e um consumo médio
     calculado sobre um pedaço da história é um número diferente do consumo da
     máquina — dizer sobre o que a conta foi feita é metade do indicador. */
  const lancamentosLidos = abastecimentos.length;
  const lancamentosTotais = consultaAbastecimentos.data?.total ?? lancamentosLidos;
  const recorte =
    lancamentosTotais > lancamentosLidos
      ? `sobre os ${formatarInteiro(lancamentosLidos)} lançamentos mais recentes, de ${formatarInteiro(lancamentosTotais)}`
      : `sobre os ${formatarInteiro(lancamentosLidos)} lançamentos da empresa`;

  return (
    <HubDoCombustivel>
      {/* Só o que a faixa do hub não diz (p.17). O "Volume" saiu: era o "Volume
          lançado" do hub com outro rótulo. O recorte da conta, que morava no
          contexto dele, passou para a linha acima da tabela. */}
      {/* Tom da marca: ter consumo calculado não é status de máquina, e o
          verde (bloco e barra) lia como "Operando" (p.09; R02, p.31: o bloco
          leva o tom do assunto). */}
      <FaixaIndicadores>
        <CardIndicador
          rotulo="Com consumo calculado"
          icone={Truck}
          tom="marca"
          carregando={carregandoConsumo}
          valor={carregandoConsumo ? '' : formatarInteiro(comBase)}
          {...(porAtivo.length > 0 ? { proporcao: comBase / porAtivo.length } : {})}
          contexto={carregandoConsumo ? '' : `de ${formatarInteiro(porAtivo.length)} máquinas têm leitura suficiente`}
        />
        <CardIndicador
          rotulo="Fora da faixa"
          icone={Fuel}
          tom="parada"
          carregando={carregandoConsumo}
          alerta={foraDaFaixa > 0}
          valor={carregandoConsumo ? '' : formatarInteiro(foraDaFaixa)}
          contexto="máquinas cujo consumo médio não bate com a faixa cadastrada"
        />
      </FaixaIndicadores>

      {/* "Sobre os 0 lançamentos" antes da resposta é um número errado (p.32):
          a linha é esqueleto, e a tabela anuncia a espera. */}
      {carregandoConsumo ? (
        <Barra className="my-1 h-3 w-1/2" />
      ) : (
        <p className="txt-apoio text-pretty text-tinta-secundaria">{`Consumo médio calculado ${recorte}.`}</p>
      )}

      <Tabela
        rotulo="Consumo por equipamento"
        colunas={totalSobreTudo(COLUNAS, porAtivo)}
        linhas={paginaDoConsumo.visiveis}
        rodape={<RodapeTabela {...paginaDoConsumo.rodape} />}
        alturaDupla
        chaveDaLinha={(c) => c.codigo}
        carregando={consultaAbastecimentos.isPending || consultaCadastros.isPending}
        rotuloDoTotal="Soma"
        erro={
          consultaAbastecimentos.isError ? (
            <EstadoVazio
              titulo="Não foi possível calcular o consumo"
              descricao="Os lançamentos não chegaram. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
              acao={
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    void consultaAbastecimentos.refetch();
                  }}
                >
                  Carregar os lançamentos
                </Botao>
              }
            />
          ) : null
        }
        vazio={
          <EstadoVazio
            titulo="Nenhum consumo para mostrar"
            descricao="O consumo aparece a partir do segundo abastecimento de cada máquina, quando há duas leituras para comparar."
            acao={
              podeLancar ? (
                <Link to="/combustivel/lancamento" search={{}} className="botao botao-secundario">
                  Lançar abastecimento
                </Link>
              ) : undefined
            }
          />
        }
      />

      {/* "Registrar lubrificante" abre em JANELA (pedido do dono, 25/09): o formulário
          fixo ao lado da tabela tinha o próprio laranja, e o laranja da tela
          é o "Lançar abastecimento" do hub. A ação mora na linha de título da
          tabela a que pertence (p.15: ações no cabeçalho), e não solta entre
          as duas tabelas. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="txt-secao text-tinta">Lubrificante aplicado</h2>
        {podeRegistrarLubrificante ? (
        <JanelaDeAcaoComRodape
          rotulo="Registrar lubrificante"
          titulo="Registrar lubrificante"
          aberta={registrando}
          aoAbrir={() => {
            setRegistrando(true);
          }}
          aoFechar={() => {
            setRegistrando(false);
          }}
        >
          <FormularioDeLubrificante
            aoCancelar={() => {
              setRegistrando(false);
            }}
            equipamentos={equipamentos}
            produtos={(consultaCadastros.data?.produtos ?? []).filter(
              (p) => p.categoria === 'lubrificante',
            )}
            carregandoCadastros={consultaCadastros.isPending}
          />
        </JanelaDeAcaoComRodape>
        ) : null}
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-1">
          <Tabela
            rotulo="Aplicações de lubrificante"
            colunas={COLUNAS_LUBRIFICANTE}
            linhas={paginaDosLubrificantes.visiveis}
            rodape={<RodapeTabela {...paginaDosLubrificantes.rodape} />}
            alturaDupla
            chaveDaLinha={(a) => a.id}
            carregando={consultaLubrificantes.isPending}
            erro={
              consultaLubrificantes.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar as aplicações"
                  descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void consultaLubrificantes.refetch();
                      }}
                    >
                      Carregar as aplicações
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              <EstadoVazio
                titulo="Nenhuma aplicação registrada"
                descricao={
                  podeRegistrarLubrificante
                    ? 'Registre a primeira aplicação de óleo ou graxa para acompanhar o que entrou em cada máquina.'
                    : 'Quem registra óleo e graxa é a Oficina ou o Almoxarifado. A aplicação aparece aqui depois de registrada.'
                }
                acao={
                  podeRegistrarLubrificante ? (
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        setRegistrando(true);
                      }}
                    >
                      Registrar lubrificante
                    </Botao>
                  ) : undefined
                }
              />
            }
          />
        </div>

      </div>
    </HubDoCombustivel>
  );
}

function FormularioDeLubrificante({
  equipamentos,
  produtos,
  carregandoCadastros,
  aoCancelar,
}: {
  readonly aoCancelar: () => void;
  readonly equipamentos: readonly EquipamentoDoModulo[];
  readonly produtos: readonly { readonly id: string; readonly nome: string; readonly unidade: string }[];
  /** As listas ainda não chegaram: os controles são esqueleto, e não listas vazias (p.32). */
  readonly carregandoCadastros: boolean;
}) {
  const base = useId();
  /* A máquina com a foto da frota, como no lançamento de abastecimento. */
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const registrar = useRegistrarLubrificante();

  const [equipamento, setEquipamento] = useState('');
  const [produto, setProduto] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [preco, setPreco] = useState('');
  const [ponto, setPonto] = useState('');
  /* DECISÃO 13: o depósito de onde o lubrificante sai. A lista vem do módulo
     de estoque; quem não vê o estoque não recebe o campo, e a aplicação fica
     só como fato, como sempre foi. */
  const estoque = useApoioDoEstoque();
  /* Enquanto a lista vem, o campo é esqueleto — mas só para quem lê o
     estoque: para os outros a rota recusa e o campo nunca aparece, e um
     esqueleto que some não reserva lugar, empurra. */
  const permissoes = useSessaoEmMemoria()?.permissoes ?? null;
  const esperaDepositos = estoque.isPending && (permissoes === null || permissoes.includes('estoque.ler'));
  const [deposito, setDeposito] = useState('');
  const [quando, setQuando] = useState(() => paraCampoDataHora(new Date()));
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [pronto, setPronto] = useState<string | null>(null);

  const escolhido = produtos.find((p) => p.id === produto) ?? null;

  /* A mesma regra no `onBlur` e no envio (p.16): o erro aparece debaixo do
     campo ao sair dele, e o envio mostra todos de uma vez. */
  function mensagemDe(campo: string): string | null {
    switch (campo) {
      case 'equipamento':
        return equipamento ? null : 'Escolha a máquina que recebeu o lubrificante.';
      case 'produto':
        return produto ? null : 'Escolha o produto aplicado.';
      case 'quando':
        return interpretarDataHora(quando) === null ? 'Informe a data e a hora da aplicação.' : null;
      case 'quantidade': {
        const valor = interpretarNumero(quantidade);
        return valor === null || valor <= 0 ? 'Informe a quantidade aplicada.' : null;
      }
      case 'preco': {
        /* O preço é opcional, mas preço digitado ERRADO não passa: sem esta
           conferência um "4,5,0" virava nada e a aplicação era gravada sem
           preço, sem uma palavra na tela. O campo em branco é ausência
           declarada; texto que não é número é engano, e engano se avisa. */
        const informado = interpretarNumero(preco);
        return preco.trim() !== '' && (informado === null || informado < 0)
          ? 'Informe o preço em reais, como 32,90. Deixe em branco se não souber.'
          : null;
      }
      default:
        return null;
    }
  }

  function conferir(campo: string) {
    setErros((atual) => comErroDe(atual, campo, mensagemDe(campo)));
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setPronto(null);

    const instante = interpretarDataHora(quando);
    const valor = interpretarNumero(quantidade);
    const precoInformado = interpretarNumero(preco);

    const conferidos = conferirTodos(['equipamento', 'produto', 'quando', 'quantidade', 'preco'], mensagemDe);
    setErros(conferidos);
    if (temErro(conferidos) || instante === null || valor === null) return;

    registrar.mutate(
      {
        equipamentoId: equipamento,
        produtoId: produto,
        quantidade: valor,
        ocorridoEm: instante.toISOString(),
        ...(preco.trim() !== '' && precoInformado !== null ? { precoUnitario: precoInformado } : {}),
        ...(ponto.trim() ? { ponto: ponto.trim() } : {}),
        ...(deposito ? { depositoId: deposito } : {}),
        motivo: 'aplicação de lubrificante pela tela',
        contexto: (() => {
          const maquina = equipamentos.find((e) => e.id === equipamento);
          return {
            ...(maquina ? { alvo: { codigo: maquina.codigo, nome: `${maquina.tipo} ${maquina.modelo ?? ''}`.trim() } } : {}),
            ...(escolhido ? { fatos: [escolhido.nome] } : {}),
          };
        })(),
      },
      {
        /* Gravou, fecha (pedido do dono, 03/10/2026, no combustível): o aviso
           de sucesso da camada de chamadas diz o que entrou, e a janela
           aberta convidava a lançar de novo o que já foi lançado. */
        onSuccess: () => {
          aoCancelar();
        },
      },
    );
  }

  const falha = registrar.isError ? explicarFalha(registrar.error) : null;

  return (
    <form id={`${base}-formulario`} onSubmit={enviar} noValidate className="formulario-grade">
      {/* A recusa no TOPO do formulário (p.22). */}
      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {falha.erro}. {falha.comoResolver}
        </p>
      ) : null}
      <p className="campo-linha txt-apoio text-tinta-secundaria">
        O registro guarda o fato: que óleo entrou, em qual ponto, de qual máquina. Com o depósito escolhido, o
        lubrificante também sai do estoque, e sem saldo lá a aplicação é recusada.
      </p>

      <Campo id={`${base}-equipamento`} rotulo="Equipamento" erro={erros.equipamento ?? null}>
        {carregandoCadastros ? (
          <ControleCarregando oQue="Carregando a frota" />
        ) : (
          /* A máquina com o código interno e a placa em etiqueta (pedido do
             dono, 31/10/2026), no seletor da mesa (p.23, p.31). */
          <ListaDeEscolha
            id={`${base}-equipamento`}
            rotulo="Equipamento"
            valor={equipamento}
            aoMudar={(valor) => {
              setEquipamento(valor);
              setErros((atual) => comErroDe(atual, 'equipamento', null));
            }}
            aoSair={() => {
              conferir('equipamento');
            }}
            rotuloVazio="Escolha o equipamento"
            opcoes={comVisualDaFrota(equipamentos.map((e) => opcaoDaMaquina(e)))}
            idDoErro={erros.equipamento ? `${base}-equipamento-erro` : undefined}
            adesivo
          />
        )}
      </Campo>

      <Campo id={`${base}-produto`} rotulo="Produto" erro={erros.produto ?? null}>
        {carregandoCadastros ? (
          <ControleCarregando oQue="Carregando os produtos" />
        ) : (
          /* O seletor do sistema, com a unidade em etiqueta: litro para óleo,
             quilo para graxa. Com um lubrificante só, ele já vem escolhido. */
          <ListaDeEscolha
            id={`${base}-produto`}
            rotulo="Produto"
            valor={produto}
            aoMudar={(valor) => {
              setProduto(valor);
              setErros((atual) => comErroDe(atual, 'produto', null));
            }}
            aoSair={() => {
              conferir('produto');
            }}
            rotuloVazio="Escolha o produto"
            opcoes={produtos.map((p) => ({
              valor: p.id,
              rotulo: p.nome,
              ...(p.unidade ? { etiquetas: [{ texto: p.unidade, tom: 'neutro' as const, titulo: 'Unidade' }] } : {}),
            }))}
            idDoErro={erros.produto ? `${base}-produto-erro` : undefined}
          />
        )}
      </Campo>

      <Campo
        id={`${base}-quantidade`}
        rotulo="Quantidade"
        ajuda="A unidade é a do produto: litro para óleo, quilo para graxa."
        erro={erros.quantidade ?? null}
      >
        <div className="flex items-center gap-2">
          <input
            id={`${base}-quantidade`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={quantidade}
            placeholder="18"
            className="controle w-full"
            onChange={(evento) => {
              setQuantidade(evento.target.value);
              setErros((atual) => comErroDe(atual, 'quantidade', null));
            }}
            onBlur={() => {
              conferir('quantidade');
            }}
            {...marcaDeErro(erros, 'quantidade', `${base}-quantidade`)}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">{escolhido?.unidade ?? 'L'}</span>
        </div>
      </Campo>

      <Campo
        id={`${base}-preco`}
        rotulo="Preço unitário"
        opcional
        ajuda="O preço da nota, por litro ou por quilo. Sem ele o custo desta aplicação fica de fora das contas."
        erro={erros.preco ?? null}
      >
        <div className="flex items-center gap-2">
          <span className="txt-corpo shrink-0 text-tinta-apagada">R$</span>
          <input
            id={`${base}-preco`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={preco}
            placeholder="32,90"
            className="controle w-full"
            onChange={(evento) => {
              setPreco(evento.target.value);
              setErros((atual) => comErroDe(atual, 'preco', null));
            }}
            onBlur={() => {
              conferir('preco');
            }}
            {...marcaDeErro(erros, 'preco', `${base}-preco`)}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">/{escolhido?.unidade ?? 'L'}</span>
        </div>
      </Campo>

      {esperaDepositos ? (
        <Campo id={`${base}-deposito`} rotulo="Sai do depósito" opcional ajuda="Sem depósito, a aplicação não baixa o estoque.">
          <ControleCarregando oQue="Carregando os depósitos" />
        </Campo>
      ) : estoque.data && estoque.data.depositos.length > 0 ? (
        <Campo id={`${base}-deposito`} rotulo="Sai do depósito" opcional ajuda="Sem depósito, a aplicação não baixa o estoque.">
          <ListaDeEscolha
            id={`${base}-deposito`}
            rotulo="Sai do depósito"
            valor={deposito}
            aoMudar={setDeposito}
            rotuloVazio="Sem baixa de estoque"
            opcoes={estoque.data.depositos.map((d) => ({ valor: d.id, rotulo: d.nome }))}
          />
        </Campo>
      ) : null}

      <Campo id={`${base}-ponto`} rotulo="Ponto de aplicação" opcional>
        <input
          id={`${base}-ponto`}
          type="text"
          autoComplete="off"
          value={ponto}
          placeholder="Motor, hidráulico, transmissão"
          className="controle w-full"
          onChange={(evento) => {
            setPonto(evento.target.value);
          }}
        />
      </Campo>

      <Campo id={`${base}-quando`} rotulo="Data e hora" erro={erros.quando ?? null}>
        <input
          id={`${base}-quando`}
          type="datetime-local"
          value={quando}
          className="controle w-full"
          onChange={(evento) => {
            setQuando(evento.target.value);
            setErros((atual) => comErroDe(atual, 'quando', null));
          }}
          onBlur={() => {
            conferir('quando');
          }}
          {...marcaDeErro(erros, 'quando', `${base}-quando`)}
        />
      </Campo>

      {pronto ? (
        <p role="status" className="campo-linha txt-apoio rounded-controle border border-borda bg-rebaixada px-4 py-2 text-tinta">
          {pronto}
        </p>
      ) : null}

      {/* Dentro da janela, a ação dela é a laranja; "Cancelar" é terciário à
          esquerda (p.15), o mesmo desenho em todas as janelas do módulo, no
          rodapé fixo da janela (R01, p.29). */}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao nivel="primario" type="submit" form={`${base}-formulario`} disabled={registrar.isPending}>
          Registrar lubrificante
        </Botao>
      </AcoesNoRodape>
    </form>
  );
}
